/**
 * ChatComposer — shared textarea + send button core used by both 1:1 twin chat
 * and room chat. Surface-specific extras are injected via slots:
 *   - `headerSlot`  — rendered above the textarea (e.g. ModeToggle in rooms)
 *   - `footerSlot`  — rendered below the textarea row (e.g. image upload in 1:1)
 *   - `beforeSendSlot` — rendered in the bottom-RIGHT cluster, left of the send button
 *   - `aboveSlot`   — top pane of the same composer surface (e.g. a pending
 *                     approval gate). One rounded box; a hairline divides the
 *                     pane from the textarea. Hidden when the slot is empty.
 *
 * `density="thin"` (the room thread) renders a thinner body instead: one row
 * (`leadingSlot`, text, send cluster) while the text fits a line, growing to
 * text over controls once it wraps, has a newline, carries `attachmentsSlot`
 * or is the hero. Status text (send hint, command error) sits in a slim row
 * under the card. The regular density is unchanged.
 *
 * When `participants` is provided, typing `@` triggers a WhatsApp-style
 * autocomplete dropdown so users can address specific people.
 */

import React, { useState, useCallback, useEffect, useId, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Loader2, Square } from "@digital-twins/design-system/icons";
import Link from "@/routing/Link";

import { useAutosizeTextarea } from "@/lib/hooks/use-autosize-textarea";
import {
  CHAT_TEXTAREA_MAX_HEIGHT_PX,
  syncTextareaAutosizeHeight,
} from "@/lib/utils/sync-textarea-autosize-height";
import { MessageLengthHint, messageCharacterCount } from '@/components/composer/MessageLengthHint';
import { ComposerAppearance, COMPOSER_SURFACE_CLASS, COMPOSER_ROUND_ACTION_CLASS, composerFlatSurfaceClassName } from "@/components/composer/composerAppearance";
import { cn } from "@/lib/utils";
import {
  MentionAutocomplete,
  formatMentionInsertion,
  getMentionToken,
  useMentionAutocomplete,
  type MentionParticipant,
} from "./MentionAutocomplete";
import { MentionHighlightTextarea } from "./MentionHighlightTextarea";
import {
  CommandAutocomplete,
  CommandPreviewPlacement,
  filterCommandOptions,
  CommandOption,
} from "./CommandAutocomplete";
import {
  findKnownSlashToken,
  findTrailingSlashToken,
  replaceSlashToken,
  stripSlashToken,
} from "@/lib/composer/slashCommandToken";
import { useTypedSuggestion } from "./composerSuggestion";
import { useComposerGrowth } from "./useComposerGrowth";

/** The hero composer grows taller before it scrolls, like the Home Lab one. */
const HERO_TEXTAREA_MAX_HEIGHT_PX = 280;

export interface ChatComposerProps {
  onSend: (text: string) => void;
  /** Preserve oversized pastes, explain the limit, and block sending. */
  messageLimit?: number;
  disabled?: boolean;
  /**
   * A turn is already running. The textarea stays writable so the reader can
   * draft the next ask while they wait. Send is blocked; when `onStop` is
   * provided the send slot becomes the stop control. Distinct from `disabled`,
   * which locks the whole surface (closed room, guest, view-only).
   */
  isBusy?: boolean;
  /**
   * When provided, the send button becomes a stop control for as long as
   * `isBusy` is true, so a surface that streams can cancel a turn in flight.
   * Omit on surfaces that cannot abort — the spinner stays, as before.
   */
  onStop?: () => void;
  /** Message shown inside the composer when in locked/guest mode (replaces the entire textarea + send). */
  disabledMessage?: string;
  /** Href for the CTA in the locked state (defaults to /login). */
  disabledCtaHref?: string;
  /** Label for the CTA button in the locked state (defaults to "Log in"). */
  disabledCtaLabel?: string;
  placeholder?: string;
  /** Rendered inside the composer box above the textarea (e.g. ModeToggle). */
  headerSlot?: React.ReactNode;
  /** Rendered in the bottom row alongside the send button (e.g. ChatFileUpload). */
  footerSlot?: React.ReactNode;
  /**
   * Bottom-right PRIMARY action (e.g. a "Looks right, continue" confirm CTA). Rendered at the
   * extreme right of the footer row IN PLACE of the send button while the input is empty; once
   * the user types a refinement the send arrow takes over (there is now something to send). Omit
   * for the plain send-only composer. Pair with `secondaryAction` for the muted link beside it.
   */
  primaryAction?: React.ReactNode;
  /**
   * Bottom-right SECONDARY action (e.g. "Skip" / "Edit creatives") — a quiet link that always sits
   * just left of the primary action / send button when provided. Only meaningful with `primaryAction`.
   */
  secondaryAction?: React.ReactNode;
  /**
   * Bottom-right ADORNMENT — a persistent control (e.g. a send-target toggle) that sits at the far
   * left of the right-hand action cluster, so it reads as belonging to the send button rather than
   * to the left-hand authoring pills. Unlike `primaryAction`/`secondaryAction` it is beat-independent:
   * it stays put as the CTA swaps to the send arrow.
   */
  beforeSendSlot?: React.ReactNode;
  /**
   * Top pane of the composer surface, not a sibling card. Empty output (a
   * slot that returns null) collapses so the hairline and extra padding go
   * with it.
   */
  aboveSlot?: React.ReactNode;
  /**
   * Quiet box: a hairline border instead of the heavy drop shadow. For surfaces
   * that float the composer over their own content and do not want a slab of
   * shadow sitting on the page behind it. Omit for the original raised box.
   */
  flat?: boolean;
  /**
   * The Home Lab composer look: a taller card on the home composer tokens, held by a
   * hairline instead of a shadow, with the larger type. For a composer that stands
   * alone in the middle of the page (an empty conversation). Wins over `flat`.
   */
  hero?: boolean;
  /**
   * Starts as ONE LINE — textarea and send button side by side — and opens to a
   * taller field on focus, then grows with the text. For surfaces that keep
   * their controls outside the box (the thread composer), where a permanently
   * tall empty box is just empty box. Omit for the stacked layout, which every
   * surface with a populated `footerSlot` still wants.
   */
  collapsible?: boolean;
  /** Whether to require text before sending (false = allow sending with only footerSlot content). */
  requireText?: boolean;
  /**
   * When set, SENDING is blocked (button disabled, Enter swallowed) and this short hint
   * renders beside the send button saying why (e.g. "Uploading video…" while a creative is
   * still hosting; sending then would run the study without its stimulus). Typing stays
   * enabled; only the send affordance is gated. Omit for normal behavior.
   */
  sendBlockedHint?: string;
  /**
   * When it returns true for the current text, `sendBlockedHint` is lifted for that text
   * (a room message with `@all` makes every twin active, so it is sendable with every twin off).
   */
  liftsSendBlock?: (text: string) => boolean;
  /** Called when the textarea receives focus. */
  onFocus?: () => void;
  /**
   * When provided, the composer accepts files via clipboard paste (Ctrl/Cmd+V)
   * and drag-and-drop onto the box, forwarding the raw File[] here. Pair with
   * useChatFileIngest in the parent so paste/drop share the Attach button's
   * compression/PDF/validation path. Omit to disable paste/drop entirely.
   */
  onAddFiles?: (files: Array<File>) => void;
  /** Room participants for @mention autocomplete. Omit for 1:1 or when not needed. */
  participants?: Array<MentionParticipant>;
  /**
   * Slash commands this surface understands. When provided, a leading `/` opens the
   * CommandAutocomplete dropdown and a `/`-leading message is routed to `onCommand`
   * instead of `onSend`. Omit (or withhold, e.g. while a workflow is armed) and every
   * command path here goes inert — a `/`-leading message is then ordinary prose.
   */
  commands?: Array<CommandOption>;
  /**
   * Handles a `/`-leading message: receives the command name (`/report`) and the trimmed
   * remainder of the line. Return `true` when the command was recognised and acted on —
   * the composer clears and never calls `onSend`. Return `false` for an unknown command:
   * the text is kept (so a second Enter works once a slow command list resolves) and an
   * inline "No such command" note renders under the box. Never falls through to `onSend`.
   */
  onCommand?: (command: string, rest: string) => boolean;
  /** Chrome for the slash list. Threads lobby passes `internal`. */
  commandAppearance?: ComposerAppearance;
  /** Thread columns pass `above` so the preview is not clipped by the pane. */
  commandPreviewPlacement?: CommandPreviewPlacement;
  className?: string;
  dataTestId?: string;
  textareaDataTestId?: string;
  /**
   * External prefill: when `seed.nonce` changes, the composer replaces its text with
   * `seed.text` and focuses the textarea (so a caller can drop a suggested message in for the
   * user to tweak + send). The nonce lets the same text re-apply on repeat picks.
   */
  seed?: { text: string; nonce: number; keepDraft?: boolean } | null;
  /** Test id of the `sendBlockedHint` text. Default `chat-composer-send-hint`. */
  sendBlockedHintDataTestId?: string;
  /**
   * Room picks (Step 11b): called with the participant ids of the @mentions inserted
   * from the autocomplete that are still in the text. Fires on insert, on a deleted
   * mention and on an unlink, never on the clear after a send.
   */
  onMentionsChange?: (participantIds: Array<string>) => void;
  /** Unlink one inserted mention (the text stays, plain); applied when `nonce` changes. */
  mentionUnlinkRequest?: { participantId: string; nonce: number } | null;
  /**
   * Insert one participant's mention, exactly as an @ menu pick does, then focus the
   * textarea; applied when `nonce` changes (the profile card's Ask action). `shouldFocus:
   * false` inserts without moving focus (the @ picker's checklist stays open).
   */
  mentionPrefillRequest?: { participantId: string; nonce: number; shouldFocus?: boolean } | null;
  /**
   * A ready-to-send suggested message (the room's opening question). While the box is
   * empty it types in as the placeholder; once fully typed, Enter or send sends it as
   * is (never alongside attachments, i.e. when `requireText` is false). The forward
   * arrow (Right in LTR, Left in RTL) drops it into the box to edit, and typing
   * anything replaces it.
   */
  suggestion?: string | null;
  /**
   * `thin`: the one-row composer that grows only when the content needs it (see the
   * file header). `collapsible` and `footerSlot` are ignored in thin (use `leadingSlot`
   * and `attachmentsSlot`), and `density` itself is ignored on the `disabledMessage`
   * path. Default `regular`.
   */
  density?: "regular" | "thin";
  /** Thin only: the start of the row (the attach "+"), under the text once grown. */
  leadingSlot?: React.ReactNode;
  /**
   * Thin only: a tray above the text (attachment tiles). Pass it only when it has
   * content: its presence forces the grown layout.
   */
  attachmentsSlot?: React.ReactNode;
  /**
   * Thin only: a shorter placeholder for phones (below `sm`), shown while the row is
   * idle (empty, not grown, no suggestion typing in), where the full one would clip
   * between the "+" and the send cluster.
   */
  compactPlaceholder?: string;
}

interface LinkedMention {
  participantId: string;
  token: string;
}

/** The document reads right to left, so the "forward" arrow is ArrowLeft. */
function isRtlDocument(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  return (document.documentElement.getAttribute("dir") ?? document.dir ?? "").toLowerCase() === "rtl";
}

/** Tailwind's `sm` breakpoint, mirrored for the one prop CSS cannot swap (a placeholder). */
const BELOW_SM_QUERY = "(max-width: 639.98px)";

function matchesBelowSm(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return Boolean(window.matchMedia(BELOW_SM_QUERY)?.matches);
}

/** True below `sm`; only subscribes while `isEnabled`. */
function useIsBelowSm(isEnabled: boolean): boolean {
  const [isBelowSm, setIsBelowSm] = useState(() => isEnabled && matchesBelowSm());
  useEffect(() => {
    if (!isEnabled || typeof window === "undefined" || typeof window.matchMedia !== "function") {
      setIsBelowSm(false);
      return;
    }
    const query = window.matchMedia(BELOW_SM_QUERY);
    setIsBelowSm(Boolean(query?.matches));
    const onChange = () => setIsBelowSm(Boolean(query.matches));
    query?.addEventListener?.("change", onChange);
    return () => query?.removeEventListener?.("change", onChange);
  }, [isEnabled]);
  return isBelowSm;
}

/** An Enter that only confirms an IME composition (CJK input), never a send. */
function isImeComposing(e: React.KeyboardEvent): boolean {
  return Boolean((e.nativeEvent as KeyboardEvent).isComposing) || e.keyCode === 229;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Mentions whose `@token` is still a whole word in the text. */
export function keepPresentMentions(
  links: Array<LinkedMention>,
  text: string,
): Array<LinkedMention> {
  return links.filter((link) =>
    new RegExp(`(^|[^\\w])@${escapeRegExp(link.token)}(?!\\w)`).test(text),
  );
}

export function ChatComposer(props: ChatComposerProps) {
  const {
    onSend,
    messageLimit,
    disabled = false,
    isBusy = false,
    onStop,
    disabledMessage,
    disabledCtaHref = "/login",
    disabledCtaLabel,
    placeholder,
    headerSlot,
    footerSlot,
    primaryAction,
    secondaryAction,
    beforeSendSlot,
    aboveSlot,
    flat = false,
    hero = false,
    collapsible = false,
    requireText = true,
    onFocus,
    onAddFiles,
    participants = [],
    commands,
    onCommand,
    commandAppearance,
    commandPreviewPlacement,
    className,
    dataTestId = "chat-composer",
    textareaDataTestId = "chat-composer-input",
    seed = null,
    sendBlockedHint: sendBlockedHintProp,
    liftsSendBlock,
    sendBlockedHintDataTestId = "chat-composer-send-hint",
    onMentionsChange,
    mentionUnlinkRequest = null,
    mentionPrefillRequest = null,
    suggestion: suggestionProp = null,
    density = "regular",
    leadingSlot,
    attachmentsSlot,
    compactPlaceholder,
  } = props;

  const { t } = useTranslation(["app", "common"]);
  const resolvedCtaLabel = disabledCtaLabel ?? t("chatChrome.logIn");
  const resolvedPlaceholder = placeholder ?? t("chatChrome.writeAnswer");

  const [value, setValue] = useState("");
  const suggestion = suggestionProp?.trim() || null;
  const isSuggestionReady = Boolean(suggestion) && value === "";
  const typedSuggestion = useTypedSuggestion(suggestion, value === "");
  /* The suggestion sends only once it reads in full, and never with attachments
     (`requireText` false): an attachment-only send must not pick up its text. */
  const isSuggestionSendable =
    requireText && isSuggestionReady && suggestion !== null && typedSuggestion === suggestion;
  const suggestionId = useId();
  const lengthHintId = useId();
  const lengthCheckedValue = value || (isSuggestionSendable ? suggestion ?? "" : "");
  const messageTooLong = messageLimit !== undefined && messageCharacterCount(lengthCheckedValue) > messageLimit;
  const sendBlockedHint =
    sendBlockedHintProp && !liftsSendBlock?.(value) ? sendBlockedHintProp : undefined;
  const [dragActive, setDragActive] = useState(false);
  /* Only read in `collapsible` mode: the box opens on focus and stays open
     while there is something in it, so it never shuts under a half-typed
     message the moment the user clicks a control outside it. */
  const [isFocused, setIsFocused] = useState(false);
  /* Set when `onCommand` rejected the typed name. Cleared on the next edit, so the
     note never outlives the text that earned it. */
  const [hasCommandError, setHasCommandError] = useState(false);
  /* Escape closes the dropdown without clearing the text. Reset on the next edit,
     so carrying on typing brings the list back rather than stranding the user. */
  const [isCommandDismissed, setIsCommandDismissed] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // External prefill: apply `seed.text` when its nonce changes (a fresh pick), then focus +
  // place the caret at the end so the user can edit straight away. Keyed on the nonce so it
  // re-applies even when the same text is picked twice; never fires on initial mount (no seed).
  const seedNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!seed || seed.nonce === seedNonceRef.current) return;
    seedNonceRef.current = seed.nonce;
    if (seed.keepDraft && textareaRef.current?.value.trim()) return;
    setValue(seed.text);
    const el = textareaRef.current;
    if (el) {
      el.focus();
      const end = seed.text.length;
      requestAnimationFrame(() => el.setSelectionRange(end, end));
    }
  }, [seed]);

  useAutosizeTextarea(textareaRef, value, {
    maxHeightPx: hero ? HERO_TEXTAREA_MAX_HEIGHT_PX : CHAT_TEXTAREA_MAX_HEIGHT_PX,
    disabled,
  });

  const isThin = density === "thin";
  const isBelowSm = useIsBelowSm(isThin && Boolean(compactPlaceholder));
  const hasTray = attachmentsSlot !== undefined && attachmentsSlot !== null && attachmentsSlot !== false;
  const thinGridRef = useRef<HTMLDivElement>(null);
  const isGrown = useComposerGrowth({
    isEnabled: isThin,
    textareaRef,
    containerRef: thinGridRef,
    value,
    isHero: hero,
    hasAttachments: hasTray,
  });
  /* The autosize hook only re-runs on a value change, but a row/grown flip changes
     the textarea's width, so its height is re-synced after the flip lands. */
  useLayoutEffect(() => {
    if (!isThin || disabled) {
      return;
    }
    const el = textareaRef.current;
    if (el) {
      syncTextareaAutosizeHeight(el, hero ? HERO_TEXTAREA_MAX_HEIGHT_PX : CHAT_TEXTAREA_MAX_HEIGHT_PX);
    }
  }, [isThin, isGrown, disabled, hero]);

  // Add @all option when there are 2+ participants
  const mentionParticipants = React.useMemo(() => {
    if (participants.length < 2) return participants;
    return [
      // The label is translated; the inserted token is always `@all`, the one Nest parses (D4).
      { id: "__all__", displayName: t("chatChrome.mentionAll"), imageUrl: null, mentionToken: "all" },
      ...participants,
    ];
  }, [participants, t]);

  const linkedMentionsRef = useRef<Array<LinkedMention>>([]);
  const emittedMentionsKeyRef = useRef("");
  // Tokens painted as tags in the textarea; the ref above stays the source for the picks.
  const [linkedTokens, setLinkedTokens] = useState<Array<string>>([]);

  const emitLinkedMentions = useCallback(
    (links: Array<LinkedMention>) => {
      linkedMentionsRef.current = links;
      setLinkedTokens(links.map((link) => link.token));
      const ids = [...new Set(links.map((link) => link.participantId))];
      const key = [...ids].sort().join(",");
      if (key === emittedMentionsKeyRef.current) return;
      emittedMentionsKeyRef.current = key;
      onMentionsChange?.(ids);
    },
    [onMentionsChange],
  );

  const handleMentionInsert = useCallback(
    (participantId: string, token: string) => {
      if (participantId === "__all__") return;
      emitLinkedMentions([...linkedMentionsRef.current, { participantId, token }]);
    },
    [emitLinkedMentions],
  );

  const unlinkNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!mentionUnlinkRequest || mentionUnlinkRequest.nonce === unlinkNonceRef.current) return;
    unlinkNonceRef.current = mentionUnlinkRequest.nonce;
    emitLinkedMentions(
      linkedMentionsRef.current.filter(
        (link) => link.participantId !== mentionUnlinkRequest.participantId,
      ),
    );
  }, [mentionUnlinkRequest, emitLinkedMentions]);

  // Ask action: the same `@token ` an @ menu pick inserts, appended to any draft and linked
  // as a pick, then focus with the caret at the end. Already in the text: link and focus only.
  const valueRef = useRef(value);
  valueRef.current = value;
  const prefillNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!mentionPrefillRequest || mentionPrefillRequest.nonce === prefillNonceRef.current) return;
    prefillNonceRef.current = mentionPrefillRequest.nonce;
    const participant = mentionParticipants.find(
      (p) => p.id === mentionPrefillRequest.participantId,
    );
    if (!participant) return;
    const token = getMentionToken(participant, mentionParticipants);
    const current = valueRef.current;
    const isPresent =
      keepPresentMentions([{ participantId: participant.id, token }], current).length > 0;
    const next = isPresent
      ? current
      : `${current}${current && !/\s$/.test(current) ? " " : ""}${formatMentionInsertion(token)}`;
    if (!isPresent) {
      setValue(next);
    }
    handleMentionInsert(participant.id, token);
    if (mentionPrefillRequest.shouldFocus === false) return;
    const el = textareaRef.current;
    if (el) {
      el.focus();
      requestAnimationFrame(() => el.setSelectionRange(next.length, next.length));
    }
  }, [mentionPrefillRequest, mentionParticipants, handleMentionInsert]);

  const mention = useMentionAutocomplete(
    mentionParticipants,
    value,
    setValue,
    textareaRef,
    handleMentionInsert,
  );

  /* What follows the leading `/`, or null when no command dropdown belongs on screen.
     The dropdown lives only while the text is still JUST the command token: the moment
     any whitespace is typed the user is writing arguments (`/report do it now`), and a
     still-open dropdown would swallow their Enter and replace the whole line with the
     bare command. No whitespace also means the `/` is necessarily on the first line. */
  /* Slack's convention: the token can sit anywhere, so long as its `/` opens
     the text or follows whitespace (`a/b` and URLs stay prose), and the list
     closes the moment whitespace follows the token. */
  const trailingToken =
    commands &&
    commands.length > 0 &&
    onCommand &&
    isFocused &&
    !isCommandDismissed
      ? findTrailingSlashToken(value)
      : null;
  const commandQuery = trailingToken ? trailingToken.name.slice(1) : null;

  const commandRows =
    commandQuery === null
      ? []
      : filterCommandOptions(commands ?? [], commandQuery);
  const hasCommandRows = commandRows.length > 0;

  /* Typed the whole name already. Enter then RUNS it rather than completing it:
     a reader who typed `/report` in full and pressed Enter meant to run the
     thing, and making them press Enter twice to get past their own exact match
     is the kind of tax nobody thanks you for. Only an incomplete name lets the
     dropdown claim the key. */
  /* Claimed ONLY when the typed name is the whole story: an exact match that is
     also the only row. With names derived from slugs (plan D1) an org can seed
     `/test-plus` beside `/test`, and then the reader may well have arrowed down
     to the longer one — taking Enter off the dropdown there would run the row
     they did not highlight. Compared case-insensitively, because that is how
     `filterCommandOptions` decided which rows to show. */
  const isExactCommand =
    trailingToken !== null &&
    commandRows.length === 1 &&
    commandRows[0].command.toLowerCase() === trailingToken.name.toLowerCase();

  const handleCommandSelect = useCallback((option: CommandOption) => {
    // Trailing space so the picked path and the typed path leave the composer in
    // exactly the same state — including closing the dropdown, which keys off
    // "no whitespace after the token". The token is replaced IN PLACE so the
    // words typed before it survive as the message.
    const token = findTrailingSlashToken(value);
    const picked = token
      ? replaceSlashToken(value, token, `${option.command} `)
      : `${option.command} `;
    setValue(picked);
    setHasCommandError(false);
    const el = textareaRef.current;
    if (el) {
      el.focus();
      requestAnimationFrame(() =>
        el.setSelectionRange(picked.length, picked.length),
      );
    }
  }, [value]);

  const handleSend = useCallback(() => {
    // An empty box with a fully typed suggestion (and no attachments) sends it as is.
    const text = value.trim() || (isSuggestionSendable && suggestion ? suggestion : "");
    // `sendBlockedHint` gates Enter too: the disabled button alone would leave the
    // keyboard path open, and a send mid-upload runs the study without its stimulus.
    if ((requireText && !text) || disabled || isBusy || sendBlockedHint || messageTooLong) return;

    // A leading `/` makes the whole line a command, never prose — an unknown one is
    // swallowed rather than forwarded to the assistant. The text survives a rejection
    // so a second Enter works once a still-loading command list arrives.
    const token = onCommand
      ? findKnownSlashToken(
          text,
          (commands ?? []).map((option) => option.command),
        )
      : null;
    if (onCommand && token) {
      const name = token.name;
      const rest = stripSlashToken(text, token);
      if (!onCommand(name, rest)) {
        setHasCommandError(true);
        return;
      }
      setHasCommandError(false);
      // Only the token leaves the box: the words around it stay visible while the
      // command's confirm card is open, and cancelling the card loses nothing.
      setValue(rest);
      mention.dismiss();
      return;
    }

    onSend(text);
    setValue("");
    mention.dismiss();
    // The text is gone because it was sent, not because a mention was deleted:
    // reset silently so the sent picks stay with the page until it clears them.
    linkedMentionsRef.current = [];
    emittedMentionsKeyRef.current = "";
    setLinkedTokens([]);
  }, [
    value,
    disabled,
    isBusy,
    onSend,
    onCommand,
    commands,
    requireText,
    mention,
    sendBlockedHint,
    messageTooLong,
    isSuggestionSendable,
    suggestion,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Enter that confirms an IME composition never sends (or picks a row).
      if (e.key === "Enter" && isImeComposing(e)) {
        return;
      }

      // Let mention autocomplete consume keyboard events first
      if (mention.handleKeyDown(e)) return;

      // CommandAutocomplete navigates and picks from a DOCUMENT-level listener, which
      // React's root-level handlers beat. Without yielding here, Enter would send the
      // raw `/report` text before the dropdown ever got to pick a row.
      if (
        hasCommandRows &&
        !e.shiftKey &&
        (e.key === "ArrowUp" ||
          e.key === "ArrowDown" ||
          ((e.key === "Enter" || e.key === "Tab") && !isExactCommand))
      ) {
        return;
      }

      /* Taking Enter/Tab back off a dropdown that is still on screen (the
         exact-match case). `preventDefault` alone is not enough: the dropdown
         listens on `document`, above React's root container, so without
         stopping propagation it would ALSO see this key, pick the row, and
         write the command back into the box we just cleared. */
      if (
        (e.key === "Enter" || e.key === "Tab") &&
        !e.shiftKey &&
        hasCommandRows &&
        isExactCommand
      ) {
        e.preventDefault();
        e.stopPropagation();
        handleSend();
        return;
      }

      // The forward arrow (Right in LTR, Left in RTL) drops the suggestion into the
      // empty box to edit, caret at the end. Tab keeps moving focus.
      if (
        isSuggestionReady &&
        suggestion &&
        !e.shiftKey &&
        !e.altKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        e.key === (isRtlDocument() ? "ArrowLeft" : "ArrowRight")
      ) {
        e.preventDefault();
        setValue(suggestion);
        const el = textareaRef.current;
        if (el) {
          requestAnimationFrame(() => el.setSelectionRange(suggestion.length, suggestion.length));
        }
        return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, mention, hasCommandRows, isExactCommand, isSuggestionReady, suggestion],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      setHasCommandError(false);
      setIsCommandDismissed(false);
      if (linkedMentionsRef.current.length > 0) {
        emitLinkedMentions(keepPresentMentions(linkedMentionsRef.current, newValue));
      }
      if (participants.length > 0) {
        mention.handleChange(
          newValue,
          e.target.selectionStart ?? newValue.length,
        );
      }
    },
    [participants.length, mention, emitLinkedMentions],
  );

  // Clipboard paste of image/file content (Ctrl/Cmd+V). Only swallow the paste
  // when it actually carries files, so pasting plain text still works.
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (disabled || !onAddFiles) return;
      const files: Array<File> = [];
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        onAddFiles(files);
      }
    },
    [disabled, onAddFiles],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !onAddFiles) return;
      if (Array.from(e.dataTransfer.types).includes("Files"))
        setDragActive(true);
    },
    [disabled, onAddFiles],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled || !onAddFiles) return;
      if (Array.from(e.dataTransfer.types).includes("Files"))
        e.preventDefault();
    },
    [disabled, onAddFiles],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Ignore leave events bubbling from children still inside the box.
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      setDragActive(false);
      if (disabled || !onAddFiles) return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        e.preventDefault();
        onAddFiles(files);
      }
    },
    [disabled, onAddFiles],
  );

  const canSend =
    !disabled && !isBusy && !sendBlockedHint && !messageTooLong && (!requireText || !!value.trim() || isSuggestionSendable);
  const showStop = isBusy && Boolean(onStop);

  /* Collapsible mode only opens on actual content, not on mere focus: a click
     into an empty box should not itself grow it, so the placeholder keeps
     reading as a compact, vertically centered line until there is something
     to say. `isFocused` still drives dropdowns (mentions/commands) above. */
  const isOpen = !collapsible || value.length > 0;
  const canBeHero = props.hero !== undefined;
  const isCompact = collapsible && !isOpen;

  // a11y: the keyboard focus indicator lives on the card's own border, not on the
  // textarea, and stays a neutral grey (never the brand accent) — border/60 idle,
  // full border on focus, eased by transition-colors.
  const shellClass = cn(
    COMPOSER_SURFACE_CLASS,
    // A composer that switches looks (the thread's, leaving its empty-state hero)
    // crossfades its surface over the same beat as the glide down.
    canBeHero && "duration-300",
    // The thread's docked composer keeps the hero's white card in light mode (the
    // token is unset in dark mode, so the brand surface shows there).
    canBeHero && !hero && "bg-[var(--hero-composer-bg,hsl(var(--twin-chat-surface)))]",
    hero
      ? "border border-[var(--hero-composer-border,var(--home-lab-composer-border))] bg-[var(--hero-composer-bg,var(--home-lab-composer-bg))] [box-shadow:var(--hero-composer-shadow,none)]"
      : flat
        ? composerFlatSurfaceClassName(isFocused)
        : isThin
          ? "shadow-[0_6px_20px_rgba(0,0,0,0.10)]"
          : "shadow-[0px_15px_30px_0px_rgba(0,0,0,0.2)]",
  );
  const bodyClass = hero ? "px-5 py-4 md:px-6 md:py-5" : cn("px-5", isCompact ? "py-2.5" : "py-4");

  const showMention =
    mention.mentionState?.active && mention.filteredParticipants.length > 0;

  // `@all` is never a linked pick, but it is still a tag wherever the menu offers it.
  const hasAllMention = mentionParticipants.some((p) => p.id === "__all__");
  const mentionTags = hasAllMention ? [...linkedTokens, "all"] : linkedTokens;

  /* One source for what both densities' textareas share (handlers, aria, refs,
     common attributes), so a change here can never skip the thin path. Each
     density adds only its own className. */
  const textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement> &
    React.RefAttributes<HTMLTextAreaElement> & { "data-testid": string } = {
    ref: textareaRef,
    "data-testid": textareaDataTestId,
    value,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onPaste: onAddFiles ? handlePaste : undefined,
    onFocus: () => {
      setIsFocused(true);
      onFocus?.();
    },
    onBlur: () => setIsFocused(false),
    placeholder: suggestion ? typedSuggestion : resolvedPlaceholder,
    "aria-describedby": [isSuggestionReady ? suggestionId : null, messageLimit === undefined ? null : lengthHintId].filter(Boolean).join(" ") || undefined,
    disabled,
    maxLength: messageLimit === undefined ? 4000 : undefined,
    "aria-invalid": messageTooLong || undefined,
    rows: 1,
  };

  /* The send slot for both densities: stop while a stoppable turn runs, the
     primary action on an empty box, else send (spinner while busy). Only the
     shape differs: a rounded square in regular, a circle in thin. */
  const renderSendControl = (sizeClass: string) => {
    const shapeClass = isThin
      ? COMPOSER_ROUND_ACTION_CLASS
      : "flex items-center justify-center rounded-lg";
    if (showStop) {
      return (
        <button
          type="button"
          data-testid="chat-composer-stop"
          onClick={onStop}
          aria-label={t("chatChrome.stopGenerating")}
          className={cn(
            shapeClass,
            "bg-primary text-primary-foreground transition-opacity hover:opacity-90",
            sizeClass,
          )}
        >
          <Square
            className="h-3 w-3 fill-current"
            strokeWidth={0}
            aria-hidden="true"
          />
        </button>
      );
    }
    if (primaryAction && !canSend && !sendBlockedHint && !messageTooLong) {
      /* While `sendBlockedHint` is set, canSend is false for the BLOCKED reason, not
       the empty-input reason, so the confirm CTA must not stand in for send. */
      return primaryAction;
    }
    return (
      <button
        type="button"
        data-testid="chat-composer-send"
        onClick={handleSend}
        disabled={!canSend}
        // a11y: when a turn is in flight (`isBusy` / locked `disabled`) keep the
        // SOLID mint button + a dark, full-contrast spinner so "querying" is
        // obviously visible; mark it aria-busy for AT. Only the can't-send-yet
        // idle state (empty input) reads muted-and-disabled.
        aria-label={isBusy || disabled ? t("chatChrome.sending") : t("common:send")}
        aria-busy={isBusy || disabled}
        className={cn(
          shapeClass,
          "transition-colors",
          sizeClass,
          isBusy || disabled
            ? "bg-primary text-primary-foreground"
            : "bg-primary text-primary-foreground disabled:bg-twin-chat-bubble disabled:text-twin-chat-send-fg",
        )}
      >
        {isBusy || disabled ? (
          <Loader2
            className="h-4 w-4 animate-spin text-primary-foreground"
            strokeWidth={2.75}
            aria-hidden="true"
          />
        ) : (
          <ArrowRight
            className="h-4 w-4"
            strokeWidth={2.5}
            aria-hidden="true"
          />
        )}
      </button>
    );
  };

  if (disabled && disabledMessage) {
    return (
      <div className={cn("shrink-0", className)} data-testid={dataTestId}>
        <div className={shellClass}>
          <div className={bodyClass}>
            <div className="min-h-[24px] w-full text-base text-twin-chat-fg select-none">
              {disabledMessage}
            </div>

            <div className="mt-2 flex items-center justify-between">
              <Link
                href={disabledCtaHref}
                className="inline-flex items-center gap-1.5 rounded-lg bg-twin-chat-send px-4 py-1.5 text-sm font-semibold text-twin-chat-send-fg transition-opacity hover:opacity-90"
              >
                {resolvedCtaLabel}
                <ArrowRight
                  className="h-4 w-4"
                  strokeWidth={2.5}
                  aria-hidden="true"
                />
              </Link>

              <button
                type="button"
                disabled
                aria-label={t("common:send")}
                // Locked send: read as clearly disabled (muted bubble, not a faded mint
                // arrow that still looks live) without dropping to near-invisible. The arrow
                // uses the dark send-fg token so it stays black on the accent in dark mode.
                className="flex h-8 w-8 items-center justify-center rounded-lg bg-twin-chat-bubble text-twin-chat-send-fg"
              >
                <ArrowRight
                  className="h-4 w-4"
                  strokeWidth={2.5}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isThin) {
    const isRowEmpty = !isGrown && value === "";
    const thinPlaceholder =
      compactPlaceholder && isBelowSm && isRowEmpty && !suggestion
        ? compactPlaceholder
        : textareaProps.placeholder;
    return (
      <ThinComposerBody
        className={className}
        dataTestId={dataTestId}
        shellClass={cn(shellClass, "rounded-3xl")}
        isHero={hero}
        isGrown={isGrown}
        gridRef={thinGridRef}
        dragHandlers={
          onAddFiles
            ? {
                onDragEnter: handleDragEnter,
                onDragOver: handleDragOver,
                onDragLeave: handleDragLeave,
                onDrop: handleDrop,
              }
            : undefined
        }
        dragOverlay={
          dragActive ? (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] border-2 border-dashed border-twin-chat-send bg-twin-chat-surface/85 text-sm font-medium text-twin-chat-fg">
              {t("chatChrome.dropImageToAttach")}
            </div>
          ) : null
        }
        aboveSlot={aboveSlot}
        headerSlot={headerSlot}
        attachmentsSlot={hasTray ? attachmentsSlot : null}
        autocompletes={
          <>
            {showMention && (
              <MentionAutocomplete
                participants={mention.filteredParticipants}
                query={mention.mentionState?.query ?? ""}
                onSelect={mention.handleSelect}
                onDismiss={mention.dismiss}
              />
            )}
            {commandQuery !== null && (
              <CommandAutocomplete
                options={commands ?? []}
                query={commandQuery}
                onSelect={handleCommandSelect}
                onDismiss={() => setIsCommandDismissed(true)}
                appearance={commandAppearance}
                previewPlacement={commandPreviewPlacement}
              />
            )}
          </>
        }
        textarea={
          <MentionHighlightTextarea
            textareaProps={{ ...textareaProps, placeholder: thinPlaceholder }}
            tags={mentionTags}
            wrapperClassName="min-w-0 [grid-area:text]"
            caretClassName={
              hero
                ? "caret-[color:var(--hero-composer-fg,hsl(var(--twin-chat-fg)))]"
                : "caret-twin-chat-fg"
            }
            className={cn(
              "block min-h-[36px] w-full min-w-0 resize-none bg-transparent px-2 py-1.5 text-base text-twin-chat-fg placeholder:text-twin-chat-fg-muted focus:outline-none focus-visible:outline-none focus-visible:ring-0",
              hero
                ? "min-h-[72px] max-h-[280px] text-[15px] md:text-[16px] text-[color:var(--hero-composer-fg,hsl(var(--twin-chat-fg)))] placeholder:text-[var(--hero-composer-placeholder-fg,var(--home-lab-composer-placeholder-fg))]"
                : "max-h-[120px]",
              // An empty row never wraps its placeholder, so a long one cannot inflate it.
              isRowEmpty && "overflow-hidden whitespace-nowrap",
              // Grown, the controls sit right under the text: a tighter bottom keeps
              // two lines near 110px.
              isGrown && !hero && "pb-0.5",
              "leading-6",
            )}
          />
        }
        suggestionHint={
          isSuggestionReady ? (
            <span id={suggestionId} className="sr-only" data-testid="chat-composer-suggestion">
              {suggestion}
              {isSuggestionSendable && !disabled && (
                <>{" "}{t(isRtlDocument() ? "chatChrome.suggestionHintRtl" : "chatChrome.suggestionHint")}</>
              )}
            </span>
          ) : null
        }
        leadingSlot={leadingSlot}
        trailing={
          <>
            {beforeSendSlot}
            {secondaryAction}
            {renderSendControl("h-9 w-9")}
          </>
        }
        commandError={hasCommandError ? t("chatChrome.noSuchCommand") : null}
        sendBlockedHint={sendBlockedHint ?? null}
        sendBlockedHintDataTestId={sendBlockedHintDataTestId}
        lengthHint={<MessageLengthHint value={lengthCheckedValue} limit={messageLimit} id={lengthHintId} />}
      />
    );
  }

  return (
    <div className={cn("shrink-0", className)} data-testid={dataTestId}>
      <div
        className={shellClass}
        onDragEnter={onAddFiles ? handleDragEnter : undefined}
        onDragOver={onAddFiles ? handleDragOver : undefined}
        onDragLeave={onAddFiles ? handleDragLeave : undefined}
        onDrop={onAddFiles ? handleDrop : undefined}
      >
        <div
          className="empty:hidden border-b border-border/50 px-5 pt-3"
          data-testid="chat-composer-above"
        >
          {aboveSlot}
        </div>

        <div className={cn("relative", bodyClass)}>
        {dragActive && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-twin-chat-send bg-twin-chat-surface/85 text-sm font-medium text-twin-chat-fg">
            {t("chatChrome.dropImageToAttach")}
          </div>
        )}

        {headerSlot && <div className="mb-2">{headerSlot}</div>}

        {showMention && (
          <MentionAutocomplete
            participants={mention.filteredParticipants}
            query={mention.mentionState?.query ?? ""}
            onSelect={mention.handleSelect}
            onDismiss={mention.dismiss}
          />
        )}

        {commandQuery !== null && (
          <CommandAutocomplete
            options={commands ?? []}
            query={commandQuery}
            onSelect={handleCommandSelect}
            onDismiss={() => setIsCommandDismissed(true)}
            appearance={commandAppearance}
            previewPlacement={commandPreviewPlacement}
          />
        )}

        {/* One row while collapsed (text and send side by side, the way a
            single-line input reads), stacked once there are controls under the
            text. `items-end` so a growing message pushes the box up and leaves
            the send button where the hand left it. */}
        <div
          className={
            collapsible
              ? cn("flex gap-3", isCompact ? "items-center" : "items-end")
              : undefined
          }
        >
          <MentionHighlightTextarea
            textareaProps={textareaProps}
            tags={mentionTags}
            wrapperClassName={cn("w-full", collapsible && "min-w-0 flex-1")}
            caretClassName={
              hero
                ? "caret-[color:var(--hero-composer-fg,hsl(var(--twin-chat-fg)))]"
                : "caret-twin-chat-fg"
            }
            className={cn(
              "block max-h-[120px] min-h-[24px] w-full resize-none bg-transparent text-base text-twin-chat-fg placeholder:text-twin-chat-fg-muted focus:outline-none focus-visible:outline-none focus-visible:ring-0",
              collapsible &&
                "min-w-0 flex-1 transition-[min-height] duration-200",
              collapsible && isOpen && "min-h-[48px]",
              canBeHero && "transition-[min-height] duration-300 ease-out",
              hero &&
                "min-h-[72px] max-h-[280px] text-[15px] md:text-[16px] text-[color:var(--hero-composer-fg,hsl(var(--twin-chat-fg)))] placeholder:text-[var(--hero-composer-placeholder-fg,var(--home-lab-composer-placeholder-fg))]",
            )}
          />
          {isSuggestionReady && (
            <span id={suggestionId} className="sr-only" data-testid="chat-composer-suggestion">
              {suggestion}
              {/* The keys only work once the placeholder has typed in, and never while disabled. */}
              {isSuggestionSendable && !disabled && (
                <>{" "}{t(isRtlDocument() ? "chatChrome.suggestionHintRtl" : "chatChrome.suggestionHint")}</>
              )}
            </span>
          )}

          <div
            className={cn(
              "flex items-center justify-between gap-3",
              collapsible ? "shrink-0" : "mt-2",
            )}
          >
            <div className="min-w-0">{footerSlot}</div>

            {/* Right-aligned action cluster: a quiet secondary link (Skip / Edit …) then the ONE
              primary action. When a `primaryAction` (confirm CTA) is wired AND the input is empty,
              it stands in for the send button at the extreme right; the moment the user types a
              refinement the send arrow takes over (there is now something to send). With no
              primaryAction this is just the send button, exactly as before. */}
            <div className="flex shrink-0 items-center gap-3">
              {sendBlockedHint && (
                <span
                  data-testid={sendBlockedHintDataTestId}
                  aria-live="polite"
                  className="text-xs text-twin-chat-fg-muted"
                >
                  {sendBlockedHint}
                </span>
              )}
              {beforeSendSlot}
              {secondaryAction}
              {renderSendControl(isCompact ? "h-7 w-7" : "h-8 w-8")}
            </div>
          </div>
        </div>
        </div>
      </div>

      {hasCommandError && (
        <div
          data-testid="chat-composer-command-error"
          aria-live="polite"
          className="mt-1.5 px-1 text-xs text-twin-chat-fg-muted"
        >
          {t("chatChrome.noSuchCommand")}
        </div>
      )}
      <MessageLengthHint value={lengthCheckedValue} limit={messageLimit} id={lengthHintId} />
    </div>
  );
}

interface ThinComposerDragHandlers {
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

interface ThinComposerBodyProps {
  lengthHint?: React.ReactNode;
  className?: string;
  dataTestId: string;
  shellClass: string;
  isHero: boolean;
  isGrown: boolean;
  gridRef: React.RefObject<HTMLDivElement>;
  dragHandlers?: ThinComposerDragHandlers;
  dragOverlay: React.ReactNode;
  aboveSlot?: React.ReactNode;
  headerSlot?: React.ReactNode;
  attachmentsSlot: React.ReactNode;
  autocompletes: React.ReactNode;
  textarea: React.ReactNode;
  suggestionHint: React.ReactNode;
  leadingSlot?: React.ReactNode;
  trailing: React.ReactNode;
  commandError: string | null;
  sendBlockedHint: string | null;
  sendBlockedHintDataTestId: string;
}

/**
 * Thin density layout only; state and handlers stay in ChatComposer. One grid
 * holds the textarea and the controls in both layouts, so the textarea is never
 * remounted (focus, caret, IME and autosize survive the switch):
 *   row:   "lead text trail"
 *   grown: "text text" / "lead trail"
 * DOM order is textarea, lead, trail, so Tab runs text, +, stepper, send. In the
 * row, Shift+Tab from the text therefore skips the "+" drawn before it (as in
 * ChatGPT's composer).
 */
function ThinComposerBody(props: ThinComposerBodyProps) {
  const {
    className,
    dataTestId,
    shellClass,
    isHero,
    isGrown,
    gridRef,
    dragHandlers,
    dragOverlay,
    aboveSlot,
    headerSlot,
    attachmentsSlot,
    autocompletes,
    textarea,
    suggestionHint,
    leadingSlot,
    trailing,
    commandError,
    sendBlockedHint,
    sendBlockedHintDataTestId,
    lengthHint,
  } = props;

  const hasBelow = Boolean(commandError) || Boolean(sendBlockedHint);

  return (
    <div className={cn("shrink-0", className)} data-testid={dataTestId}>
      <div
        className={shellClass}
        data-testid="chat-composer-shell"
        data-layout={isGrown ? "grown" : "row"}
        {...dragHandlers}
      >
        {dragOverlay}

        <div
          className="empty:hidden border-b border-border/50 px-5 pt-3"
          data-testid="chat-composer-above"
        >
          {aboveSlot}
        </div>

        <div className={cn("relative", isHero ? "p-3 md:p-4" : "p-2")}>
          {headerSlot && <div className="mb-2">{headerSlot}</div>}

          {autocompletes}

          {attachmentsSlot && (
            <div className="px-2 pb-2" data-testid="chat-composer-tray">
              {attachmentsSlot}
            </div>
          )}

          <div
            ref={gridRef}
            data-testid="chat-composer-grid"
            className={cn(
              "grid items-end gap-1",
              isGrown
                ? "grid-cols-[auto_minmax(0,1fr)] [grid-template-areas:'text_text'_'lead_trail']"
                : "grid-cols-[auto_minmax(0,1fr)_auto] [grid-template-areas:'lead_text_trail']",
            )}
          >
            {textarea}
            {suggestionHint}
            <div className="flex min-h-[36px] items-center [grid-area:lead]" data-testid="chat-composer-lead">
              {leadingSlot}
            </div>
            <div
              className="flex min-h-[36px] shrink-0 items-center gap-2 justify-self-end [grid-area:trail]"
              data-testid="chat-composer-trail"
            >
              {trailing}
            </div>
          </div>
        </div>
      </div>

      {/* Always mounted, so a screen reader hears the text that lands in it; only the
          text inside swaps. Empty, it takes no space. */}
      <div aria-live="polite" data-testid="chat-composer-live">
        {hasBelow && (
          <div
            data-testid="chat-composer-below"
            className="mt-1.5 flex items-start gap-3 px-3 text-xs text-twin-chat-fg-muted"
          >
            {commandError && <div data-testid="chat-composer-command-error">{commandError}</div>}
            {sendBlockedHint && (
              <span data-testid={sendBlockedHintDataTestId} className="ms-auto text-end">
                {sendBlockedHint}
              </span>
            )}
          </div>
        )}
      </div>
      {lengthHint}
    </div>
  );
}
